import React, { useState } from 'react';
import { ArrowLeft, Cloud, ServerCog, Mail, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProviderMeta {
  id: 'google' | 'microsoft' | 'icloud' | 'other';
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  defaults?: {
    imapHost: string;
    imapPort: number;
    smtpHost: string;
    smtpPort: number;
  };
}

const PROVIDERS: ProviderMeta[] = [
  {
    id: 'google',
    title: 'Google',
    subtitle: 'Gmail and Google Workspace accounts',
    icon: <Mail className="text-red-500" size={24} />,
  },
  {
    id: 'microsoft',
    title: 'Microsoft Exchange',
    subtitle: 'Outlook, Hotmail, and Exchange accounts',
    icon: <Mail className="text-blue-500" size={24} />,
  },
  {
    id: 'icloud',
    title: 'iCloud',
    subtitle: 'Apple iCloud email accounts',
    icon: <Cloud className="text-slate-700" size={24} />,
    defaults: {
      imapHost: 'imap.mail.me.com',
      imapPort: 993,
      smtpHost: 'smtp.mail.me.com',
      smtpPort: 587,
    },
  },
  {
    id: 'other',
    title: 'Other (Manual Setup)',
    subtitle: 'Configure manually for other email providers',
    icon: <ServerCog className="text-slate-700" size={24} />,
  },
];

const GOOGLE_OAUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const MS_OAUTH_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';

const AddAccount: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'choose' | 'manual'>('choose');
  const [provider, setProvider] = useState<ProviderMeta | null>(null);

  // Manual form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [incomingServer, setIncomingServer] = useState('');
  const [incomingPort, setIncomingPort] = useState<number | ''>('');
  const [outgoingServer, setOutgoingServer] = useState('');
  const [outgoingPort, setOutgoingPort] = useState<number | ''>('');
  const [security, setSecurity] = useState<'SSL/TLS' | 'STARTTLS'>('SSL/TLS');

  const handleProviderClick = (p: ProviderMeta) => {
    if (p.id === 'google') {
      window.location.href = GOOGLE_OAUTH_URL;
      return;
    }
    if (p.id === 'microsoft') {
      window.location.href = MS_OAUTH_URL;
      return;
    }
    // manual providers
    setProvider(p);
    if (p.defaults) {
      setIncomingServer(p.defaults.imapHost);
      setIncomingPort(p.defaults.imapPort);
      setOutgoingServer(p.defaults.smtpHost);
      setOutgoingPort(p.defaults.smtpPort);
    }
    setStep('manual');
  };

  const renderChoose = () => (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Add Email Account</h1>
      <p>Choose your email provider to get started</p>
      <div className="space-y-4">
        {PROVIDERS.map(p => (
          <button
            key={p.id}
            onClick={() => handleProviderClick(p)}
            className="w-full flex items-center gap-4 p-4 border rounded-lg hover:bg-muted"
          >
            {p.icon}
            <div className="text-left flex-1">
              <div className="font-medium">{p.title}</div>
              <div className="text-sm opacity-70">{p.subtitle}</div>
            </div>
            <LogIn size={16} className="opacity-60" />
          </button>
        ))}
      </div>
    </div>
  );

  const renderManual = () => (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-center gap-3">
        <button onClick={() => setStep('choose')} className="p-1">
          <ArrowLeft />
        </button>
        <h1 className="text-xl font-semibold">Add Email Account</h1>
      </div>

      {/* provider badge */}
      {provider && (
        <div className="flex items-center gap-3 p-3 rounded-md bg-muted/50 max-w-md">
          {provider.icon}
          <span className="font-medium">{provider.title}</span>
          <button
            onClick={() => setStep('choose')}
            className="ml-auto text-primary text-sm underline"
          >
            Change
          </button>
        </div>
      )}

      {/* form */}
      <div className="space-y-4 max-w-md">
        <div>
          <label className="block text-sm font-medium mb-1">Email Address</label>
          <input
            type="email"
            className="border p-2 rounded w-full"
            placeholder="your.email@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Password</label>
          <input
            type="password"
            className="border p-2 rounded w-full"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Display Name</label>
          <input
            type="text"
            className="border p-2 rounded w-full"
            placeholder="Your Name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
          />
        </div>

        <h2 className="font-medium mt-6">Server Settings</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Incoming Server</label>
            <input
              type="text"
              className="border p-2 rounded w-full"
              value={incomingServer}
              onChange={e => setIncomingServer(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Port</label>
            <input
              type="number"
              className="border p-2 rounded w-full"
              value={incomingPort}
              onChange={e => setIncomingPort(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Outgoing Server</label>
            <input
              type="text"
              className="border p-2 rounded w-full"
              value={outgoingServer}
              onChange={e => setOutgoingServer(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Port</label>
            <input
              type="number"
              className="border p-2 rounded w-full"
              value={outgoingPort}
              onChange={e => setOutgoingPort(Number(e.target.value))}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm mb-1">Security</label>
          <select
            className="border p-2 rounded w-full"
            value={security}
            onChange={e => setSecurity(e.target.value as any)}
          >
            <option value="SSL/TLS">SSL/TLS</option>
            <option value="STARTTLS">STARTTLS</option>
          </select>
        </div>
        <div className="flex gap-3 mt-4">
          <button
            className="px-4 py-2 border rounded"
            onClick={() => setStep('choose')}
          >
            Back
          </button>
          <button className="px-4 py-2 bg-primary text-white rounded flex-1">
            Add Account
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex justify-center p-6">
      <div className="w-full max-w-lg bg-background border rounded-xl shadow p-8">
        {step === 'choose' ? renderChoose() : renderManual()}
      </div>
    </div>
  );
};

export default AddAccount; 